/**
 * Volume computation utilities for label maps.
 *
 * Computes anatomical volumes in milliliters from vtkImageData label maps
 * by counting voxels and multiplying by voxel volume.
 */

import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';

/** Conversion factor from mm³ to mL. */
const MM3_TO_ML = 1 / 1000;

/**
 * Compute the volume in mL for a single label value in a label map.
 *
 * @param imageData - vtkImageData containing integer label values
 * @param labelValue - The label value to count
 * @returns Volume in milliliters
 */
export function computeLabelVolumeMl(imageData: vtkImageData, labelValue: number): number {
    const scalars = imageData.getPointData().getScalars();
    if (!scalars) return 0;

    const data = scalars.getData();
    const spacing = imageData.getSpacing();
    const voxelVolumeMm3 = spacing[0] * spacing[1] * spacing[2];

    let count = 0;
    for (let i = 0; i < data.length; i++) {
        if (data[i] === labelValue) {
            count++;
        }
    }

    return count * voxelVolumeMm3 * MM3_TO_ML;
}

/**
 * Compute volumes in mL for multiple label values in a single pass.
 *
 * @param imageData - vtkImageData containing integer label values
 * @param labelValues - Array of label values to count
 * @returns Map from labelValue to volume in mL
 */
export function computeAllLabelVolumes(
    imageData: vtkImageData,
    labelValues: readonly number[]
): ReadonlyMap<number, number> {
    const scalars = imageData.getPointData().getScalars();
    if (!scalars) return new Map(labelValues.map((v) => [v, 0]));

    const data = scalars.getData();
    const spacing = imageData.getSpacing();
    const voxelVolumeMm3 = spacing[0] * spacing[1] * spacing[2];

    // Use a null-prototype object for O(1) lookup during the scan (avoids prototype keys)
    const counts: Record<number, number> = Object.create(null);
    for (const lv of labelValues) {
        counts[lv] = 0;
    }

    for (let i = 0; i < data.length; i++) {
        const val = data[i];
        if (val in counts) {
            counts[val]++;
        }
    }

    const result = new Map<number, number>();
    for (const lv of labelValues) {
        result.set(lv, counts[lv] * voxelVolumeMm3 * MM3_TO_ML);
    }
    return result;
}

/**
 * Overlap volume in mL between each (tumor, segment) pair.
 *
 * Returned as `Map<tumorLabel, Map<segmentLabel, overlapMl>>`.
 */
export type TumorSegmentOverlaps = ReadonlyMap<number, ReadonlyMap<number, number>>;

/** Tolerance for floating-point comparison of grid geometry (spacing, origin). */
const GEOM_EPSILON = 1e-6;

/**
 * Check whether two vtkImageData volumes share the same grid geometry
 * (dimensions, spacing, and origin), meaning voxel indices correspond 1:1.
 */
function haveSameGrid(a: vtkImageData, b: vtkImageData): boolean {
    const aDims = a.getDimensions();
    const bDims = b.getDimensions();
    if (aDims[0] !== bDims[0] || aDims[1] !== bDims[1] || aDims[2] !== bDims[2]) return false;

    const aSpacing = a.getSpacing();
    const bSpacing = b.getSpacing();
    for (let i = 0; i < 3; i++) {
        if (Math.abs(aSpacing[i] - bSpacing[i]) > GEOM_EPSILON) return false;
    }

    const aOrigin = a.getOrigin();
    const bOrigin = b.getOrigin();
    for (let i = 0; i < 3; i++) {
        if (Math.abs(aOrigin[i] - bOrigin[i]) > GEOM_EPSILON) return false;
    }

    return true;
}

/**
 * Create an empty overlaps map (all zeros) for the given labels.
 */
function createEmptyOverlaps(
    segmentLabels: readonly number[],
    tumorLabels: readonly number[]
): TumorSegmentOverlaps {
    return new Map(tumorLabels.map((t) => [t, new Map(segmentLabels.map((s) => [s, 0]))]));
}

/**
 * Compute overlap volumes between tumor and segment label maps.
 *
 * For every tumor voxel that spatially coincides with a segment voxel,
 * the overlap is accumulated for that (tumor, segment) pair.
 *
 * If both label maps share the same grid (dimensions, spacing, origin),
 * a fast index-to-index scan is used. Otherwise, each tumor voxel is mapped
 * to world coordinates and looked up in the segment grid.
 *
 * @param couinaudImageData - Couinaud segment label map (labels 1–8)
 * @param tumorImageData - Tumor label map
 * @param segmentLabels - Segment label values to track
 * @param tumorLabels - Tumor label values to track
 * @returns Nested map: tumorLabel → segmentLabel → overlap volume in mL
 */
export function computeTumorSegmentOverlaps(
    couinaudImageData: vtkImageData,
    tumorImageData: vtkImageData,
    segmentLabels: readonly number[],
    tumorLabels: readonly number[]
): TumorSegmentOverlaps {
    const segScalars = couinaudImageData.getPointData().getScalars();
    const tumScalars = tumorImageData.getPointData().getScalars();

    if (!segScalars || !tumScalars) return createEmptyOverlaps(segmentLabels, tumorLabels);

    if (haveSameGrid(couinaudImageData, tumorImageData)) {
        return computeOverlapsSameGrid(couinaudImageData, tumorImageData, segmentLabels, tumorLabels);
    }

    return computeOverlapsDifferentGrid(couinaudImageData, tumorImageData, segmentLabels, tumorLabels);
}

/**
 * Fast path: both label maps share the same grid, so flat-array indices correspond 1:1.
 */
function computeOverlapsSameGrid(
    couinaudImageData: vtkImageData,
    tumorImageData: vtkImageData,
    segmentLabels: readonly number[],
    tumorLabels: readonly number[]
): TumorSegmentOverlaps {
    const segData = couinaudImageData.getPointData().getScalars()!.getData();
    const tumData = tumorImageData.getPointData().getScalars()!.getData();
    const len = segData.length;

    const spacing = couinaudImageData.getSpacing();
    const voxelVolumeMm3 = spacing[0] * spacing[1] * spacing[2];

    const segSet: Record<number, true> = Object.create(null);
    for (const s of segmentLabels) segSet[s] = true;
    const tumSet: Record<number, true> = Object.create(null);
    for (const t of tumorLabels) tumSet[t] = true;

    const counts: Record<number, Record<number, number>> = Object.create(null);
    for (const t of tumorLabels) {
        counts[t] = Object.create(null);
        for (const s of segmentLabels) counts[t][s] = 0;
    }

    for (let i = 0; i < len; i++) {
        const tVal = tumData[i];
        const sVal = segData[i];
        if (tVal in tumSet && sVal in segSet) {
            counts[tVal][sVal]++;
        }
    }

    return countsToOverlaps(counts, segmentLabels, tumorLabels, voxelVolumeMm3);
}

/**
 * Slow path: grids differ, so each tumor voxel is mapped to world coordinates
 * and looked up in the segment grid via nearest-neighbor index conversion.
 *
 * Overlap is counted per tumor voxel (using the tumor voxel volume).
 */
function computeOverlapsDifferentGrid(
    couinaudImageData: vtkImageData,
    tumorImageData: vtkImageData,
    segmentLabels: readonly number[],
    tumorLabels: readonly number[]
): TumorSegmentOverlaps {
    const tumScalars = tumorImageData.getPointData().getScalars()!;
    const tumData = tumScalars.getData();
    const segScalars = couinaudImageData.getPointData().getScalars()!;
    const segData = segScalars.getData();

    const tumDims = tumorImageData.getDimensions() as [number, number, number];
    const tumSpacing = tumorImageData.getSpacing() as [number, number, number];
    const tumOrigin = tumorImageData.getOrigin() as [number, number, number];

    const segDims = couinaudImageData.getDimensions() as [number, number, number];
    const segSpacing = couinaudImageData.getSpacing() as [number, number, number];
    const segOrigin = couinaudImageData.getOrigin() as [number, number, number];

    const tumVoxelVolumeMm3 = tumSpacing[0] * tumSpacing[1] * tumSpacing[2];

    const tumSet: Record<number, true> = Object.create(null);
    for (const t of tumorLabels) tumSet[t] = true;
    const segSet: Record<number, true> = Object.create(null);
    for (const s of segmentLabels) segSet[s] = true;

    const counts: Record<number, Record<number, number>> = Object.create(null);
    for (const t of tumorLabels) {
        counts[t] = Object.create(null);
        for (const s of segmentLabels) counts[t][s] = 0;
    }

    for (let tz = 0; tz < tumDims[2]; tz++) {
        for (let ty = 0; ty < tumDims[1]; ty++) {
            for (let tx = 0; tx < tumDims[0]; tx++) {
                const tumIdx = tx + ty * tumDims[0] + tz * tumDims[0] * tumDims[1];
                const tVal = tumData[tumIdx];
                if (!(tVal in tumSet)) continue;

                // Tumor voxel center in world coordinates
                const wx = tumOrigin[0] + tx * tumSpacing[0];
                const wy = tumOrigin[1] + ty * tumSpacing[1];
                const wz = tumOrigin[2] + tz * tumSpacing[2];

                // Nearest-neighbor index in the segment grid
                const sx = Math.round((wx - segOrigin[0]) / segSpacing[0]);
                const sy = Math.round((wy - segOrigin[1]) / segSpacing[1]);
                const sz = Math.round((wz - segOrigin[2]) / segSpacing[2]);

                if (sx < 0 || sx >= segDims[0] || sy < 0 || sy >= segDims[1] || sz < 0 || sz >= segDims[2]) {
                    continue;
                }

                const segIdx = sx + sy * segDims[0] + sz * segDims[0] * segDims[1];
                const sVal = segData[segIdx];
                if (sVal in segSet) {
                    counts[tVal][sVal]++;
                }
            }
        }
    }

    return countsToOverlaps(counts, segmentLabels, tumorLabels, tumVoxelVolumeMm3);
}

/**
 * Convert voxel counts to overlap volumes in mL.
 */
function countsToOverlaps(
    counts: Record<number, Record<number, number>>,
    segmentLabels: readonly number[],
    tumorLabels: readonly number[],
    voxelVolumeMm3: number
): TumorSegmentOverlaps {
    const result = new Map<number, Map<number, number>>();
    for (const t of tumorLabels) {
        const inner = new Map<number, number>();
        for (const s of segmentLabels) {
            inner.set(s, counts[t][s] * voxelVolumeMm3 * MM3_TO_ML);
        }
        result.set(t, inner);
    }
    return result;
}
